import { Link } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { HelpArticle } from "@/types/help";
import { ArticleThumb } from "./ArticleThumb";
import type { HelpView } from "./HelpToolbar";

export function initials(name: string): string {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U"
  );
}

export function formatArticleDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function AuthorRow({ article }: { article: HelpArticle }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="size-10 rounded-lg border border-(--gray-200)">
        <AvatarImage src={article.author.avatarUrl} alt="" />
        <AvatarFallback className="rounded-lg text-xs">
          {initials(article.author.name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 leading-tight">
        <p className="truncate text-sm font-medium text-(--color-text-primary)">
          {article.author.name}
        </p>
        <p className="truncate text-xs text-(--gray-500)">
          {formatArticleDate(article.publishedAt)}
        </p>
      </div>
    </div>
  );
}

function ReadTime({ article }: { article: HelpArticle }) {
  const { t } = useLingui();
  const [min, max] = article.readTimeMinutes;
  return (
    <div className="flex items-center gap-2 text-sm text-(--gray-500)">
      <Clock className="size-5" />
      <span>{t`${min}-${max} min read`}</span>
    </div>
  );
}

function ArticleSummary({ article }: { article: HelpArticle }) {
  const { t } = useLingui();
  return (
    <div className="space-y-1">
      <p className="line-clamp-2 text-base leading-snug text-(--gray-500)">
        {article.excerpt}
      </p>
      <span className="text-base font-medium text-(--gray-600)">
        {t`read more...`}
      </span>
    </div>
  );
}

export function ArticleCard({
  article,
  variant,
}: {
  article: HelpArticle;
  variant: HelpView;
}) {
  const cardClass =
    "rounded-lg border border-(--gray-200) bg-(--gray-100) shadow-(--shell-shadow) transition-colors hover:bg-(--gray-200)";

  if (variant === "list") {
    return (
      <Link
        to="/help-center/$articleId"
        params={{ articleId: article.id }}
        className={cn("flex gap-3 p-3", cardClass)}
      >
        <ArticleThumb
          category={article.category}
          imageUrl={article.imageUrl}
          className="w-64.5 shrink-0 self-stretch"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-3 px-2 py-1">
          <ReadTime article={article} />
          <h3 className="text-xl font-semibold text-(--color-text-primary)">{article.title}</h3>
          <ArticleSummary article={article} />
          <Separator className="bg-(--gray-200)" />
          <AuthorRow article={article} />
        </div>
      </Link>
    );
  }

  return (
    <Link
      to="/help-center/$articleId"
      params={{ articleId: article.id }}
      className={cn("flex flex-col gap-3 p-3", cardClass)}
    >
      <ArticleThumb
        category={article.category}
        imageUrl={article.imageUrl}
        className="h-35 w-full"
      />
      <div className="flex flex-1 flex-col gap-3 px-2 pb-2">
        <ReadTime article={article} />
        <h3 className="text-xl font-semibold text-(--color-text-primary)">{article.title}</h3>
        <ArticleSummary article={article} />
        <Separator className="mt-auto bg-(--gray-200)" />
        <AuthorRow article={article} />
      </div>
    </Link>
  );
}
